import React, { FunctionComponent, useState, useEffect, useCallback, useMemo } from 'react';
import {
  TableBody,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Typography,
} from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import {
  GetScopeTypesResponse,
  ScopeType,
  TargetType,
} from '@rbx/clients/cloudAuthenticationService/v1';
import CloudAuthenticationClient from '@modules/clients/cloudAuthenticationClient';
import throwError from '@modules/utilities/error';
import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';
import useScopeTableStyles from './ScopeTable.styles';

type ScopeTableProps = {
  spec: object;
};

type TargetTypeRow = {
  targetType: string;
  requiredPart: string;
  optionalPart: string;
  description: string;
};

type ScopeRow = {
  name: string;
  targetType: string;
  description: string;
  endpoints: string[];
};

const legacyPrefix = '/legacy';

const getExternalDocsUrl = (path: string, externalDocsUrl: string) => {
  if (path.startsWith(legacyPrefix)) {
    let prefix = path.substring(0, path.indexOf('/', 2));
    prefix = prefix.replaceAll('-', '_');
    prefix = prefix.replace('/', '_');
    return externalDocsUrl.replace(prefix, '');
  }
  return externalDocsUrl;
};

const ScopeTable: FunctionComponent<React.PropsWithChildren<ScopeTableProps>> = ({
  spec: docObject,
}) => {
  const { translate } = useTranslation();
  const [doc, setDoc] = useState<OpenAPI.Document | undefined>(undefined);
  const [targetTypes, setTargetTypes] = useState<TargetType[]>([]);
  const [scopeTypes, setScopeTypes] = useState<ScopeType[]>([]);
  const { classes } = useScopeTableStyles();

  useEffect(() => {
    const parseDoc = async () => OpenAPI.parse(docObject);
    const fetchData = async (): Promise<GetScopeTypesResponse> => {
      const client = new CloudAuthenticationClient();
      return client.getScopeTypes();
    };

    const runAndSetData = async () => {
      try {
        const [parsedDoc, response] = await Promise.all([parseDoc(), fetchData()]);
        setDoc(parsedDoc);
        setTargetTypes(response.targetTypes || []);
        setScopeTypes(response.scopeTypes || []);
      } catch (error) {
        throwError(`ScopeTable: Failed to fetch or parse data: ${error}`);
      }
    };

    runAndSetData();
  }, [docObject]);

  const targetTypeRows: TargetTypeRow[] = useMemo(
    () =>
      targetTypes
        .map((targetType) => {
          if (!targetType.name || !targetType.requiredParts) {
            return null;
          }
          return {
            targetType: targetType.name,
            requiredPart: targetType.requiredParts.join(', '),
            optionalPart:
              targetType.optionalParts && targetType.optionalParts.length > 0
                ? targetType.optionalParts[0]
                : translate('Label.NA'),
            description:
              targetType.optionalParts && targetType.optionalParts.length > 0
                ? translate('Description.TargetTypeWithOptionalParts', {
                    requiredParts: `${targetType.requiredParts}s`,
                    optionalParts: `${targetType.optionalParts}s`,
                  })
                : translate('Description.TargetTypeWithNoOptionalPart', {
                    requiredParts: `${targetType.requiredParts}s`,
                  }),
          };
        })
        .filter((row) => row !== null),
    [targetTypes, translate],
  );

  const { scopeRows, endpointToExternalDocs } = useMemo<{
    scopeRows: ScopeRow[];
    endpointToExternalDocs: Record<string, string>;
  }>(() => {
    const scopeToEndpoints: Record<string, { path: string; method: string }[]> = {};
    const externalDocsMap: Record<string, string> = {};

    if (doc) {
      OpenAPI.walkOperations(doc, (operation, method, path) => {
        operation['x-roblox-scopes']?.forEach((scopeObj) => {
          const scopeName = typeof scopeObj === 'string' ? scopeObj : scopeObj?.name;
          if (scopeName) {
            if (!scopeToEndpoints[scopeName]) {
              scopeToEndpoints[scopeName] = [];
            }
            scopeToEndpoints[scopeName].push({ path, method });
          }
          if (operation.externalDocs) {
            externalDocsMap[`${method.toUpperCase()} ${path}`] = getExternalDocsUrl(
              path,
              operation.externalDocs.url,
            );
          }
        });
      });
    }

    const scopeList = scopeTypes.flatMap((scope) =>
      (scope.operationOverrides || []).map((operation) => ({
        name: `${scope.name}:${operation.name}`,
        targetType: !scope.targetType ? translate('Label.NA') : scope.targetType,
        description: operation.descriptionTranslationKey ?? '',
        endpoints: scopeToEndpoints[`${scope.name}:${operation.name}`]
          ? scopeToEndpoints[`${scope.name}:${operation.name}`].map(
              (ep) => `${ep.method.toUpperCase()} ${ep.path}`,
            )
          : [],
      })),
    );

    const openidScopeRow = {
      name: 'openid',
      targetType: translate('Label.NA'),
      description: 'OperationDescription.openidRead',
      endpoints: ['GET /oauth/v1/userinfo'],
    };

    const profileScopeRow = {
      name: 'profile',
      targetType: translate('Label.NA'),
      description: 'OperationDescription.profileRead',
      endpoints: ['GET /oauth/v1/userinfo'],
    };

    const rows = [openidScopeRow, profileScopeRow, ...scopeList].sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    return { scopeRows: rows, endpointToExternalDocs: externalDocsMap };
  }, [doc, scopeTypes, translate]);

  return (
    <React.Fragment>
      <Typography variant='h2' component='h2' marginTop='64px' marginBottom='16px'>
        {translate('Label.TargetTypes')}
      </Typography>
      <Typography
        variant='body1'
        component='p'
        color='secondary'
        marginBottom='16px'
        fontSize='18px !important'>
        {translate('Description.TargetTypes')}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell align='left'>
                <Typography variant='body1'>{translate('Label.TargetType')}</Typography>
              </TableCell>
              <TableCell align='left'>
                <Typography variant='body1'>{translate('Label.RequiredParts')}</Typography>
              </TableCell>
              <TableCell align='left'>
                <Typography variant='body1'>{translate('Label.OptionalParts')}</Typography>
              </TableCell>
              <TableCell align='left'>
                <Typography variant='body1'>{translate('Label.DescriptionCapitalized')}</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {targetTypeRows.map((row, idx) => (
              <TableRow key={row.targetType || idx}>
                <TableCell align='left' id={row.targetType}>
                  <Typography variant='body1'>{row.targetType}</Typography>
                </TableCell>
                <TableCell align='left'>
                  <Typography variant='body1'>
                    {Array.isArray(row.requiredPart)
                      ? row.requiredPart.join(', ')
                      : row.requiredPart}
                  </Typography>
                </TableCell>
                <TableCell align='left'>
                  <Typography variant='body1'>
                    {Array.isArray(row.optionalPart)
                      ? row.optionalPart.join(', ')
                      : row.optionalPart}
                  </Typography>
                </TableCell>
                <TableCell align='left'>
                  <Typography variant='body1'>{row.description}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant='h2' component='h2' marginTop='64px' marginBottom='16px'>
        {translate('Label.Scopes')}
      </Typography>
      <Typography variant='body1' color='secondary' marginBottom='16px' fontSize='18px !important'>
        {translate('Description.Scopes')}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell align='left' key='name'>
                <Typography variant='body1'>{translate('Label.Scope')}</Typography>
              </TableCell>
              <TableCell align='left' key='targetType'>
                <Typography variant='body1'>{translate('Label.TargetType')}</Typography>
              </TableCell>
              <TableCell align='left' key='description'>
                <Typography variant='body1'>{translate('Label.DescriptionCapitalized')}</Typography>
              </TableCell>
              <TableCell align='left' key='endpoints'>
                <Typography variant='body1'>{translate('Label.Endpoints')}</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scopeRows.map((row) => (
              <TableRow key={row.name} id={row.name}>
                <TableCell align='left'>
                  <Typography variant='body1'>{row.name}</Typography>
                </TableCell>
                <TableCell align='left'>
                  <a href={`#${row.targetType}`} className={classes.link}>
                    <Typography variant='body1'>{row.targetType}</Typography>
                  </a>
                </TableCell>
                <TableCell align='left'>
                  <Typography variant='body1'>{translate(row.description ?? '')}</Typography>
                </TableCell>
                {row.endpoints.length > 0 ? (
                  <TableCell align='left'>
                    <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                      {row.endpoints.map((endpoint: string) => (
                        <li key={endpoint} className={classes.endpointRoute}>
                          <a href={endpointToExternalDocs[endpoint]} className={classes.link}>
                            <Typography variant='body1'>{endpoint}</Typography>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </TableCell>
                ) : (
                  <TableCell align='left'>
                    <Typography variant='body1'>{row.endpoints}</Typography>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </React.Fragment>
  );
};

export default ScopeTable;
